import classNames from "classnames";
import { get } from "lodash";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  MdAdsClick,
  MdBolt,
  MdDynamicFeed,
  MdLogout,
  MdMarkEmailRead,
  MdPersonAdd,
  MdPhoneCallback,
  MdPhoneInTalk,
  MdPhoneMissed,
} from "react-icons/md";
import { NAME_SPACES } from "shared/locales/constants";
import { HOT_LIST } from "utils/constants";
import { formatParisTime } from "utils/helpers/date";

const { SIGNAL_TYPES } = HOT_LIST;

const SIGNAL_STYLES = {
  [SIGNAL_TYPES.ARRIVAL_ORIGIN]: { icon: MdPersonAdd, tone: "arrival" },
  [SIGNAL_TYPES.ARRIVAL_FRESHNESS]: { icon: MdBolt, tone: "arrival" },
  [SIGNAL_TYPES.INBOUND_CALL]: { icon: MdPhoneMissed, tone: "call" },
  [SIGNAL_TYPES.CALL_LATER]: { icon: MdPhoneCallback, tone: "callback" },
  [SIGNAL_TYPES.EMAIL_OPENED]: { icon: MdMarkEmailRead, tone: "email" },
  [SIGNAL_TYPES.EMAIL_CLICKED]: { icon: MdAdsClick, tone: "email" },
  [SIGNAL_TYPES.REACHED]: { icon: MdPhoneInTalk, tone: "reached" },
  [SIGNAL_TYPES.EXIT]: { icon: MdLogout, tone: "exit" },
};

const DEFAULT_STYLE = { icon: MdDynamicFeed, tone: "exit" };

const formatPoints = (points) =>
  points === null || points === undefined ? null : `+${Math.round(points)}`;

const SignalFeed = ({ signals, onSelect }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  return (
    <aside className="signal-feed">
      <header className="signal-feed__header">
        <span className="signal-feed__live" />
        <h3 className="signal-feed__title">{t("LIST.HOT_MODE.FEED.TITLE")}</h3>
        {!!signals.length && (
          <span className="signal-feed__count">{signals.length}</span>
        )}
      </header>
      {!signals.length && (
        <div className="signal-feed__empty">
          <MdDynamicFeed size={22} />
          <span>{t("LIST.HOT_MODE.FEED.EMPTY")}</span>
        </div>
      )}
      <ul className="signal-feed__list">
        {signals.map((signal) => {
          const { icon: Icon, tone } = get(
            SIGNAL_STYLES,
            signal.type,
            DEFAULT_STYLE,
          );
          const points = formatPoints(signal.points);
          return (
            <li
              key={`${signal.projectId}-${signal.type}-${signal.at}`}
              className={classNames(
                "signal-feed__event",
                `signal-feed__event--${tone}`,
                {
                  "signal-feed__event--no-impact": signal.points === 0,
                  "signal-feed__event--clickable": !!signal.contactId,
                },
              )}
              role={signal.contactId ? "button" : undefined}
              tabIndex={signal.contactId ? 0 : undefined}
              onClick={() => onSelect(signal)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelect(signal);
                }
              }}
            >
              <span className="signal-feed__icon">
                <Icon size={15} />
              </span>
              <span className="signal-feed__body">
                <span className="signal-feed__line">
                  <span className="signal-feed__name">
                    {signal.contactName}
                  </span>
                  <span className="signal-feed__time">
                    {formatParisTime(signal.at)}
                  </span>
                </span>
                <span className="signal-feed__text">
                  {t(`LIST.HOT_MODE.FEED.TYPES.${signal.type}`, {
                    type: get(signal, "contactType", ""),
                  })}
                </span>
              </span>
              <span
                className={classNames("signal-feed__points", {
                  "signal-feed__points--empty": !points,
                })}
              >
                {points}
              </span>
            </li>
          );
        })}
      </ul>
    </aside>
  );
};

SignalFeed.propTypes = {
  signals: PropTypes.arrayOf(PropTypes.object),
  onSelect: PropTypes.func,
};

SignalFeed.defaultProps = {
  signals: [],
  onSelect: () => {},
};

export default SignalFeed;
